import type { AssetRepository } from '@/features/assets/assetRepository';
import type { Asset, AssetInput } from '@/features/assets/types';
import type { CategoryRepository } from '@/features/categories/categoryRepository';
import type { Category, CategoryInput } from '@/features/categories/types';
import type { Uid } from '@/types/shared';

const ASSETS_KEY = 'stackly-assets-v2';
const ASSET_NEXT_ID_KEY = 'stackly-asset-next-id';
const CATEGORIES_KEY = 'stackly-categories';
const CATEGORY_NEXT_ID_KEY = 'stackly-category-next-id';

function loadJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function saveJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage quota exceeded — silently fail. The in-memory state is still
    // valid for the session; data just won't survive a refresh.
  }
}

/** Build a localStorage-backed `AssetRepository`. Seeds first-time visitors. */
export const createLocalStorageAssetRepository = (seed: Asset[] = []): AssetRepository => {
  let assets: Asset[] = loadJson<Asset[]>(ASSETS_KEY, seed);
  let nextId = loadJson<number>(ASSET_NEXT_ID_KEY, 1);
  const listeners = new Set<(a: Asset[]) => void>();

  const persist = (): void => {
    saveJson(ASSETS_KEY, assets);
    saveJson(ASSET_NEXT_ID_KEY, nextId);
  };

  const emit = (): void => {
    const sorted = [...assets].sort((a, b) => b.updatedAt - a.updatedAt);
    listeners.forEach((cb) => {
      cb(sorted);
    });
  };

  // Cross-tab sync: when another tab writes to localStorage, reload.
  const onStorage = (e: StorageEvent): void => {
    if (e.key === ASSETS_KEY) {
      assets = loadJson<Asset[]>(ASSETS_KEY, seed);
      nextId = loadJson<number>(ASSET_NEXT_ID_KEY, 1);
      emit();
    }
  };
  window.addEventListener('storage', onStorage);

  return {
    subscribe(_uid: Uid, onChange: (a: Asset[]) => void): () => void {
      listeners.add(onChange);
      onChange([...assets].sort((a, b) => b.updatedAt - a.updatedAt));
      return () => {
        listeners.delete(onChange);
        if (listeners.size === 0) {
          window.removeEventListener('storage', onStorage);
        }
      };
    },
    create(_uid: Uid, input: AssetInput): Promise<string> {
      const id = `local-${nextId++}`;
      const now = Date.now();
      assets.push({ ...input, id, createdAt: now, updatedAt: now });
      persist();
      emit();
      return Promise.resolve(id);
    },
    update(_uid: Uid, id: string, patch: Partial<AssetInput>): Promise<void> {
      const idx = assets.findIndex((a) => a.id === id);
      if (idx >= 0) {
        const existing = assets[idx];
        if (existing) {
          assets[idx] = { ...existing, ...patch, updatedAt: Date.now() };
          persist();
          emit();
        }
      }
      return Promise.resolve();
    },
    remove(_uid: Uid, id: string): Promise<void> {
      assets = assets.filter((a) => a.id !== id);
      persist();
      emit();
      return Promise.resolve();
    },
  };
};

/** Build a localStorage-backed `CategoryRepository`. */
export const createLocalStorageCategoryRepository = (seed: Category[] = []): CategoryRepository => {
  let categories: Category[] = loadJson<Category[]>(CATEGORIES_KEY, seed);
  let nextId = loadJson<number>(CATEGORY_NEXT_ID_KEY, 1);
  const listeners = new Set<(c: Category[]) => void>();

  const persist = (): void => {
    saveJson(CATEGORIES_KEY, categories);
    saveJson(CATEGORY_NEXT_ID_KEY, nextId);
  };

  const emit = (): void => {
    const sorted = [...categories].sort((a, b) => a.name.localeCompare(b.name));
    listeners.forEach((cb) => {
      cb(sorted);
    });
  };

  const onStorage = (e: StorageEvent): void => {
    if (e.key === CATEGORIES_KEY) {
      categories = loadJson<Category[]>(CATEGORIES_KEY, seed);
      nextId = loadJson<number>(CATEGORY_NEXT_ID_KEY, 1);
      emit();
    }
  };
  window.addEventListener('storage', onStorage);

  return {
    subscribe(_uid: Uid, onChange: (c: Category[]) => void): () => void {
      listeners.add(onChange);
      onChange([...categories].sort((a, b) => a.name.localeCompare(b.name)));
      return () => {
        listeners.delete(onChange);
        if (listeners.size === 0) {
          window.removeEventListener('storage', onStorage);
        }
      };
    },
    create(_uid: Uid, input: CategoryInput): Promise<string> {
      const id = `local-cat-${nextId++}`;
      categories.push({ id, name: input.name.trim(), isPreset: false, createdAt: Date.now() });
      persist();
      emit();
      return Promise.resolve(id);
    },
    remove(_uid: Uid, id: string): Promise<void> {
      categories = categories.filter((c) => c.id !== id);
      persist();
      emit();
      return Promise.resolve();
    },
  };
};
